import { FilePlusIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FormEvent, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Icons } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { queryKeys, useCreateSite } from "@/clients/deflow";

export const Route = createFileRoute("/plugin/figma/onboarding/2")({
  component: OnboardingStep2,
});

interface CustomElements extends HTMLFormControlsCollection {
  siteName: HTMLInputElement;
}

interface CustomForm extends HTMLFormElement {
  readonly elements: CustomElements;
}

function OnboardingStep2() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { trigger, isMutating } = useCreateSite();
  const [projectNameValid, setProjectNameValid] = useState(false);

  const onSubmit = async (event: FormEvent<CustomForm>) => {
    event.preventDefault();

    const [, error] = await trigger({
      siteName: event.currentTarget.elements.siteName.value,
      origin: "figma",
    });

    if (!error) {
      // Refresh the cached account so the next page sees the new site list
      // (the create-site mutation already invalidates queryKeys.sites; we
      // additionally invalidate the account so siteIds are reloaded).
      await queryClient.invalidateQueries({ queryKey: queryKeys.account() });
      navigate({ to: "/plugin/figma/onboarding/3", replace: true });
    }
  };

  return (
    <div>
      <div className="px-8 pt-8">
        <div className="w-full">
          <form onSubmit={onSubmit}>
            <CardHeader>
              <CardTitle className="flex">
                {`Create your first project`}
                <FilePlusIcon className="ml-2" />
              </CardTitle>
              <CardDescription>
                {`Let's give it name. You can always change it later.`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid w-full items-center gap-4">
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="name">Project name</Label>
                  <Input
                    id="siteName"
                    placeholder="Name of your project"
                    onChange={(event) => {
                      setProjectNameValid(event.target.value.length > 3);
                    }}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex">
              <Button
                className="ml-auto"
                disabled={!projectNameValid || isMutating}
              >
                {isMutating && (
                  <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                Continue
              </Button>
            </CardFooter>
          </form>
        </div>
      </div>
    </div>
  );
}
